import { ARENAS } from './maps';
import { RoomGoal, type GameState } from './types';

export interface SimCheckpoint {
  version: 1;
  state: Omit<GameState, 'grid' | 'items' | 'hazards'> & { grid: number[]; items: number[]; hazards: number[] };
  rng: [number, number, number, number];
  countdown: number;
}

/** Check persisted input before allocating a map or letting it run at boot. */
export function validCheckpoint(raw: unknown): raw is SimCheckpoint {
  const c = raw as SimCheckpoint | null;
  if (!c || c.version !== 1 || !c.state) return false;
  const s = c.state, cfg = s.settings;
  const integer = (n: unknown, min: number, max: number) => typeof n === 'number' && Number.isInteger(n) && n >= min && n <= max;
  const numbers = (v: unknown, max: number) => Array.isArray(v) && v.length <= 4096 && v.every(n => integer(n, 0, max));
  const fields = (v: unknown, numeric: string, bool = '', strings = '') => {
    if (!v || typeof v !== 'object') return false;
    const r = v as Record<string, unknown>;
    return numeric.split(' ').filter(Boolean).every(k => typeof r[k] === 'number' && Number.isFinite(r[k])) &&
      bool.split(' ').filter(Boolean).every(k => typeof r[k] === 'boolean') &&
      strings.split(' ').filter(Boolean).every(k => typeof r[k] === 'string' && (r[k] as string).length <= 200);
  };
  if (!cfg || !ARENAS.some(a => a.id === cfg.mapId) || !['ffa', 'teams', 'survival'].includes(cfg.mode) ||
    !['lives', 'kills', 'timed'].includes(cfg.winBy) ||
    !fields(cfg, 'seed roundSeconds lives itemDensity killTarget', 'suddenDeath friendlyFire') ||
    !integer(cfg.roundSeconds, 1, 36000) || !integer(cfg.lives, 1, 100) || cfg.itemDensity < 0 || cfg.itemDensity > 10 ||
    (cfg.roomGoal !== undefined && !Object.values(RoomGoal).includes(cfg.roomGoal))) return false;
  if (cfg.enemies !== undefined && (!Array.isArray(cfg.enemies) || cfg.enemies.length > 16 ||
    !cfg.enemies.every(e => e && integer(e.kind, 0, 3) && integer(e.count, 0, 100)))) return false;
  for (const k of ['catchup', 'arenaEvents', 'deflect', 'loseGearOnDeath', 'resolve'] as const) {
    if (cfg[k] !== undefined && typeof cfg[k] !== 'boolean') return false;
  }
  if (cfg.hitPoints !== undefined && !integer(cfg.hitPoints, 1, 100)) return false;
  if (!integer(s.width, 3, 64) || !integer(s.height, 3, 64) || !integer(s.tick, 0, 100_000_000) ||
    !['countdown', 'playing', 'sudden-death', 'over'].includes(s.phase) ||
    !fields(s, 'timeLeft sdIndex sdTimer exitAt wave nextBombId nextBlastId') ||
    !integer(c.countdown, 0, 90) || !Array.isArray(c.rng) || c.rng.length !== 4 ||
    !c.rng.every(n => integer(n, -2147483648, 4294967295)) || c.rng.every(n => n === 0)) return false;
  const size = s.width * s.height;
  if (!numbers(s.grid, 3) || !numbers(s.items, 255) || !numbers(s.hazards, 8) ||
    s.grid.length !== size || s.items.length !== size || s.hazards.length !== size) return false;
  const positioned = (e: { x: number; y: number }) => e.x >= 0 && e.x < s.width && e.y >= 0 && e.y < s.height;
  if (!Array.isArray(s.players) || !s.players.length || s.players.length > 8 || !s.players.every(p =>
    fields(p, 'slot team botLevel x y dir lives respawnAt maxBombs bombsActive power speedLevel carryUntil carryVx carryVy invulnUntil shieldUntil ghostUntil curse curseUntil hp resolve kills deaths score',
      'bot moving alive kick punch remote pierce launched charged prevBomb prevAction', 'id name') && positioned(p) &&
      integer(p.slot, 0, 7) && integer(p.dir, 0, 3) && integer(p.maxBombs, 1, 100) && integer(p.power, 1, 100) && integer(p.speedLevel, 0, 100))) return false;
  if (!Array.isArray(s.enemies) || s.enemies.length > 256 || !s.enemies.every(e =>
    fields(e, 'kind x y dir hp speed hurtUntil', 'alive hunted', 'id') && positioned(e) && integer(e.kind, 0, 3) && integer(e.dir, 0, 3))) return false;
  if (!Array.isArray(s.bombs) || s.bombs.length > 1024 || !s.bombs.every(b =>
    fields(b, 'id x y fuse power vx vy flight', 'pierce remote', 'ownerId') && positioned(b) &&
    Array.isArray(b.flightFrom) && b.flightFrom.length === 2 && b.flightFrom.every(Number.isFinite) &&
    Array.isArray(b.flightTo) && b.flightTo.length === 2 && b.flightTo.every(Number.isFinite) &&
    Array.isArray(b.noCollide) && b.noCollide.length <= 8 && b.noCollide.every(id => typeof id === 'string'))) return false;
  if (!Array.isArray(s.blasts) || s.blasts.length > 1024 || !s.blasts.every(b =>
    fields(b, 'id ox oy power ticksLeft', '', 'ownerId') && numbers(b.cells, size - 1) && numbers(b.kinds, 2) && numbers(b.axes, 1) &&
    b.cells.length === b.kinds.length && b.cells.length === b.axes.length)) return false;
  return Array.isArray(s.winners) && s.winners.length <= 8 && s.winners.every(id => s.players.some(p => p.id === id));
}
